import { Controller, FormProvider, useForm, type Resolver } from "react-hook-form";

import { type PatchBuildingByIdRequestBody } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { ActionButton, Alert, Select } from "@eisox/design-system";
import { ArrowRightIcon } from "@eisox/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { buildingApi } from "~/api";
import { Help } from "~/components/help";
import {
  DEFAULT_MID_SEASON_DELAY,
  DEFAULT_MID_SEASON_HEATING_TEMPERATURE,
  DEFAULT_MID_SEASON_NO_HEATING_TEMPERATURE,
} from "~/constants/season.constant";
import { getBuildingFrostFreeStatus } from "~/features/frost-free";
import {
  getInitialState,
  getPatchBuildingByIdRequestBody,
  isSummerWinterMode,
  isSummerWinterWithHalfSeasonAvailable,
  Mode,
  SummerWinter,
  updateSeasonSchema,
  type SeasonType,
} from "~/features/settings";
import { SummerWinterWithHalfSeason } from "~/features/settings/components/summer-winter-with-half-season";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/buildings/season")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { building, modules, gateways, boilerRooms, airHandlerUnits, permissions } = useBuildingContext(Route.id);

  const { mutate } = useMutation({
    mutationFn: (body: PatchBuildingByIdRequestBody) => buildingApi.update(building.id, body),
    onSuccess: async () => {
      reset({}, { keepValues: true });
      await queryClient.invalidateQueries(buildingApi.queries.byId(building.id));
    },
  });

  const summerWinterWithHalfSeasonAvailable = isSummerWinterWithHalfSeasonAvailable(gateways, modules);
  const boilerRoomsCount = boilerRooms.length;

  const methods = useForm<SeasonType>({
    defaultValues: getInitialState(building),
    //! When migrating this page to new architecture, review type between schema and SeasonType
    resolver: zodResolver(updateSeasonSchema) as Resolver<SeasonType>,
  });

  const {
    control,
    formState: { isDirty },
    watch,
    handleSubmit,
    setValue,
    reset,
  } = methods;

  const mode = watch("mode");
  const summerPeriods = watch("summerPeriods");
  const winterPeriods = watch("winterPeriods");

  const ENUM_MODE = {
    [Mode.SUMMER]: <p className={"text-base whitespace-pre-line"}>{m.season_summer_text()}</p>,
    [Mode.WINTER]: <p className={"text-base whitespace-pre-line"}>{m.season_winter_text()}</p>,
    [Mode.SUMMER_WINTER]: <SummerWinter disabled={!permissions.building?.summerModePeriod?.update} />,
    [Mode.SUMMER_WINTER_WITH_HALF_SEASON]: (
      <SummerWinterWithHalfSeason disabled={!permissions.building?.summerModePeriod?.update} />
    ),
  };

  const MODE_LABELS: Record<Mode, () => string> = {
    [Mode.SUMMER]: m.summer,
    [Mode.WINTER]: m.winter,
    [Mode.SUMMER_WINTER]: m.season_summer_winter_option,
    [Mode.SUMMER_WINTER_WITH_HALF_SEASON]: m.season_summer_winter_half_season_option,
  };

  let options: { value: string; name: string }[] = Object.keys(ENUM_MODE).map(modeKey => ({
    value: modeKey,
    name: MODE_LABELS[modeKey as Mode](),
  }));
  if (!summerWinterWithHalfSeasonAvailable) {
    options = options.filter(o => (o.value as Mode) !== Mode.SUMMER_WINTER_WITH_HALF_SEASON);
  }

  const handleChangeMode = (value: string | string[], onChange: (...event: unknown[]) => void) => {
    if ((value as string) in ENUM_MODE) {
      onChange(value);
      if (
        value === Mode.SUMMER_WINTER &&
        summerPeriods.begin &&
        summerPeriods.end &&
        winterPeriods.begin &&
        winterPeriods.end &&
        !isSummerWinterMode(summerPeriods, winterPeriods)
      ) {
        setValue("winterPeriods", {
          begin: dayjs(summerPeriods.end, "DD/MM").add(1, "day").format("DD/MM"),
          end: dayjs(summerPeriods.begin, "DD/MM").subtract(1, "day").format("DD/MM"),
        });
      }
      if (value === Mode.SUMMER_WINTER_WITH_HALF_SEASON) {
        setValue("midSeason", {
          heatingTemperature: DEFAULT_MID_SEASON_HEATING_TEMPERATURE,
          noHeatingTemperature: DEFAULT_MID_SEASON_NO_HEATING_TEMPERATURE,
          delay: DEFAULT_MID_SEASON_DELAY,
        });
      }
    }
  };

  const onSubmit = (data: SeasonType) => {
    const body: PatchBuildingByIdRequestBody = getPatchBuildingByIdRequestBody(data);
    mutate(body);
  };

  return (
    <FormProvider {...methods}>
      <form
        className="gap-5 flex h-full flex-col overflow-y-auto"
        data-testid="HouseSeasonSettings"
        onSubmit={e => void handleSubmit(onSubmit)(e)}
      >
        {getBuildingFrostFreeStatus(building.frostFree).active && (
          <Alert>
            <p>
              <ParaglideMessage
                message={m.season_alert}
                markup={{
                  link: ({ children }) => (
                    <Link
                      to={"/buildings/$buildingId/settings/buildings/holidays"}
                      params={{ buildingId: building.id }}
                      className="text-primary-blue hover:no-underline"
                    >
                      {children}
                    </Link>
                  ),
                }}
              />
            </p>
          </Alert>
        )}
        <Controller
          control={control}
          name="mode"
          render={({ field: { value, onChange } }) => (
            <Select.Root multiple={false} value={value} onValueChange={value => handleChangeMode(value, onChange)}>
              <Select.Trigger className="max-w-65.75" disabled={!permissions.building?.summerModePeriod?.update}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                <Select.Viewport>
                  {options.map(o => (
                    <Select.Item key={o.value} value={o.value}>
                      {o.name}
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Root>
          )}
        />
        {[Mode.SUMMER_WINTER, Mode.SUMMER_WINTER_WITH_HALF_SEASON].includes(mode) && (
          <div className="gap-5 flex flex-wrap text-base [&>*:nth-child(3)]:max-w-full">
            <div className="max-w-[calc(50%-20px)]">
              <h3 className="font-semibold">{`“${m.summer_mode()}“`}</h3>
              <p className="font-normal gap-2.5 flex whitespace-pre-line">{m.season_summer_mode_text()}</p>
            </div>
            <div className="max-w-[calc(50%-20px)]">
              <h3 className="font-semibold">{`“${m.winter_mode()}“`}</h3>
              <p className="font-normal gap-2.5 flex whitespace-pre-line">{m.season_winter_mode_text()}</p>
            </div>
            {mode === Mode.SUMMER_WINTER_WITH_HALF_SEASON && (
              <div className="max-w-full">
                <h3 className="font-semibold">{`“${m.mid_season_mode()}“`}</h3>
                <p className="font-normal gap-2.5 flex whitespace-pre-line">
                  {m.season_mid_season_text1({ count: boilerRoomsCount })}
                  <Help>{m.season_mid_season_tooltip()}</Help>
                </p>
                <p className="font-normal gap-2.5 flex whitespace-pre-line">{m.season_mid_season_text2()}</p>
                {airHandlerUnits.length > 0 && (
                  <p className="font-normal gap-2.5 flex whitespace-pre-line">{m.season_mid_season_text_cta()}</p>
                )}
              </div>
            )}
          </div>
        )}
        {ENUM_MODE[mode]}
        <ActionButton className="mb-0 mx-auto mt-auto" type="submit" disabled={!isDirty}>
          {m.save()} <ArrowRightIcon />
        </ActionButton>
      </form>
    </FormProvider>
  );
}
